const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'

const isObject = value => Object.prototype.toString.call(value) === '[object Object]'

const parseResponseBody = (contentType, rawText) => {
  if (!rawText) return null

  if (String(contentType || '').includes('application/json')) {
    try {
      return JSON.parse(rawText)
    } catch (_error) {
      return null
    }
  }

  return rawText
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || ''
  const text = await response.text()
  return parseResponseBody(contentType, text)
}

function xhrRequest(url, config, onUploadProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(config.method || 'GET', url, true)
    xhr.withCredentials = config.credentials === 'include'

    Object.entries(config.headers || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        xhr.setRequestHeader(key, value)
      }
    })

    xhr.upload.onprogress = event => {
      if (typeof onUploadProgress === 'function' && event.lengthComputable && event.total > 0) {
        const percent = Math.round((event.loaded / event.total) * 100)
        onUploadProgress(percent)
      }
    }

    xhr.onerror = () => {
      reject(new Error('Network error while uploading data.'))
    }

    xhr.onload = () => {
      const contentType = xhr.getResponseHeader('content-type') || ''
      const data = parseResponseBody(contentType, xhr.responseText)

      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onUploadProgress === 'function') onUploadProgress(100)
        resolve(data)
        return
      }

      const message = (data && data.message) || `Request failed: ${xhr.status}`
      const error = new Error(message)
      error.status = xhr.status
      error.data = data
      reject(error)
    }

    xhr.send(config.body === undefined ? null : config.body)
  })
}

async function apiRequest(path, options = {}) {
  const { body, headers, onUploadProgress, ...rest } = options
  const config = {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...(isObject(body) ? { 'Content-Type': 'application/json' } : {}),
      ...(headers || {})
    },
    ...rest
  }

  if (body !== undefined) {
    config.body = isObject(body) ? JSON.stringify(body) : body
  }

  const method = String(config.method || 'GET').toUpperCase()
  const shouldUseXhr =
    typeof onUploadProgress === 'function' &&
    ['POST', 'PUT', 'PATCH'].includes(method) &&
    typeof config.body === 'string'

  if (shouldUseXhr) {
    return xhrRequest(`${API_BASE_URL}${path}`, config, onUploadProgress)
  }

  const response = await fetch(`${API_BASE_URL}${path}`, config)
  const data = await parseResponse(response)

  if (!response.ok) {
    const message = (data && data.message) || `Request failed: ${response.status}`
    const error = new Error(message)
    error.status = response.status
    error.data = data
    throw error
  }

  return data
}

const withPagination = (basePath, params = {}) => {
  const searchParams = new URLSearchParams()

  if (params.page) searchParams.set('page', String(params.page))
  if (params.limit) searchParams.set('limit', String(params.limit))

  const queryString = searchParams.toString()
  return queryString ? `${basePath}?${queryString}` : basePath
}

export const authApi = {
  login(payload) {
    return apiRequest('/api/users/login', { method: 'POST', body: payload })
  },
  logout() {
    return apiRequest('/api/users/logout', { method: 'POST' })
  },
  me() {
    return apiRequest('/api/users/me')
  },
  listUsers() {
    return apiRequest('/api/users')
  },
  createUser(payload) {
    return apiRequest('/api/users', { method: 'POST', body: payload })
  },
  setUserActive(payload) {
    return apiRequest('/api/users/status', { method: 'PATCH', body: payload })
  }
}

export const galleryApi = {
  list(params = {}) {
    return apiRequest(withPagination('/api/gallery', params))
  },
  create(payload, options = {}) {
    return apiRequest('/api/gallery', { method: 'POST', body: payload, ...options })
  },
  bulkCreate(payload, options = {}) {
    return apiRequest('/api/gallery/bulk', { method: 'POST', body: payload, ...options })
  },
  update(id, payload, options = {}) {
    return apiRequest(`/api/gallery/${id}`, { method: 'PUT', body: payload, ...options })
  },
  remove(id) {
    return apiRequest(`/api/gallery/${id}`, { method: 'DELETE' })
  }
}

export const projectsApi = {
  getHeader() {
    return apiRequest('/api/projects/header')
  },
  updateHeader(payload, options = {}) {
    return apiRequest('/api/projects/header', { method: 'PUT', body: payload, ...options })
  },
  listPosts(params = {}) {
    return apiRequest(withPagination('/api/projects', params))
  },
  getPost(id) {
    return apiRequest(`/api/projects/${id}`)
  },
  createPost(payload, options = {}) {
    return apiRequest('/api/projects', { method: 'POST', body: payload, ...options })
  },
  updatePost(id, payload, options = {}) {
    return apiRequest(`/api/projects/${id}`, { method: 'PUT', body: payload, ...options })
  },
  deletePost(id) {
    return apiRequest(`/api/projects/${id}`, { method: 'DELETE' })
  },
  deleteImage(id, index) {
    return apiRequest(`/api/projects/${id}/images/${index}`, { method: 'DELETE' })
  }
}

export { API_BASE_URL, apiRequest }
